"use client";
import { CryptoChart } from "@/components/common/CryptoChart";
import TradingView from "@/components/common/TradingView";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CHART_COLORS } from '@/utils/dummySelectsData';

import {
    Loader2Icon,
    Repeat,
} from "lucide-react";
import { CurrencyIcon } from "@/components/common/CurrencyIcon";
import { useApiGet } from "@/react-queries/custom-request";
import { AvaliableBalanceTypes, LockedBalanceTypes } from "@/react-queries/queries/customers/types";
import { getAvaliableBalance, getLockedBalance } from "@/react-queries/queries/customers";
import { useEffect, useState } from "react";
import PieDiagram from "./PieDiagram";
import { DataTable } from "@/components/table/DataTable";
import { getColumns } from './Table/columns';
import { getTransactions } from "@/react-queries/queries/transactions";
import { Transaction } from "@/types/transaction-type";
import TransactionModal from "@/components/modal/TransactionModal";
import { getCryptoPricings } from "@/react-queries/queries/dashboard";
import { CryptoDiagramObject } from "@/react-queries/queries/dashboard/types";
import { useTranslations } from "next-intl";
import ErrorBoundary from "@/components/ErrorBoundaryComponent";
import { AdvancedRealTimeChart } from "react-ts-tradingview-widgets";

// const cryptos = [
//     { title: "Bitcoin" },
//     { title: "Ethereum" },
//     { title: "Litecoin" },
//     { title: "Tether" },
//     { title: "TON" },
//     { title: "SOL" },
// ];

const BalanceItem = ({ img, fullName, shortName, amount, amountUSD, type, i }: { img: string; fullName: string; shortName: string; amount: number; amountUSD: number; type: 'crypto' | 'currency', i: number; }) => {
    return (
        <div className="flex p-3 w-full justify-between items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-600">
            <div className="flex gap-3 items-center flex-1">
                <div className="relative">
                    <CurrencyIcon currencyCode={img} width={36} height={36} alt="" className="rounded-full" />
                    <span 
                        className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white dark:border-slate-800`} 
                        style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                    ></span>
                </div>
                <div className="flex flex-col">
                    <span className="font-medium text-slate-800 dark:text-slate-200">{fullName}</span>
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                        {shortName}
                    </p>
                </div>
            </div>
            <div className="flex flex-col text-right">
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {type === 'crypto' ? parseFloat(amount?.toFixed(8)).toString() : amount?.toLocaleString()}
                </span>
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                    $ {amountUSD?.toLocaleString()}
                </span>
            </div>
        </div>
    )
};

const Page = () => {
    const table_titles = useTranslations('table_titles');
    const balance_titles = useTranslations('Header.balance');
    const empty_titles = useTranslations('empties');
    const [page, setPage] = useState(1);
    const storedPerPage = JSON.parse(
        localStorage.getItem(`perPage-transactions`) as string
    );

    const [perPage, setPerPage] = useState(
        storedPerPage ? Number(storedPerPage) : 5
    );
    const { data: transactions, isLoading: transactionsIsLoading, isPending: transactionsIsPending, isFetching: transactionsIsFetching, refetch: refetchTransactions } = useApiGet<{ current_page: number; total_pages: number; transactions: Transaction[] }>(
        ['transactions', page, perPage],
        () => getTransactions(page, perPage)
    );

    const { data: cryptos } = useApiGet<CryptoDiagramObject[]>(['cryptos'], () => getCryptoPricings());

    const { data: lockedBalance, isLoading: lockedLoading, isError: lockedError, refetch: retryLocked } = useApiGet<LockedBalanceTypes[]>(['locked_balance'], getLockedBalance);
    const { data: avaliableBalance, isLoading: avaliableLoading, isError: avaliableError, refetch: retryAvaliable } = useApiGet<AvaliableBalanceTypes[]>(['avaliable_balance'], getAvaliableBalance);
    const [balanceType, setBalanceType] = useState<'locked' | 'avaliable'>('avaliable');
    const [balance, setBalance] = useState<LockedBalanceTypes[] | AvaliableBalanceTypes[]>();

    useEffect(() => {
        if (lockedBalance && avaliableBalance) {
            if (balanceType === 'locked') {
                // @ts-ignore
                setBalance(lockedBalance);
            } else {
                // @ts-ignore
                setBalance(avaliableBalance);
            }
        }
    }, [balanceType, lockedBalance, avaliableBalance]);

    return (
        <div className="pb-24 space-y-6">
         {/*    <div className="flex justify-center">
                <Card className="snap-x flex py-4  no-scrollbar gap-6 overflow-x-auto h-auto w-full bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 border-slate-200 dark:border-slate-700 shadow-sm">
                    {
                        // @ts-ignore
                        cryptos && cryptos?.length > 0 && cryptos.map((crypto, i) => (
                            <CryptoChart
                                key={crypto.title}
                                strokeColor={CHART_COLORS[i % CHART_COLORS.length]}
                                title={crypto.title}
                                data={crypto}
                            />
                        ))
                    }
                </Card>
            </div> */}

            <ErrorBoundary place="Balances">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-h-full">
                    <Card className="lg:col-span-1 p-6 bg-surface border-surface-border shadow-lg">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center py-3 gap-3 border-b border-surface-border justify-between">
                            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">{balance_titles('title')}</h2>
                            <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-700 p-1 rounded-lg">
                                <Button 
                                    onClick={() => setBalanceType('avaliable')} 
                                    variant={balanceType === 'avaliable' ? 'default' : 'ghost'}
                                    size="sm"
                                    className={balanceType === 'avaliable' ? 'bg-brand-500 hover:bg-brand-600 text-white shadow-md' : 'hover:bg-slate-200 dark:hover:bg-slate-600'}
                                >
                                    {balance_titles('available')}
                                </Button>
                                <Button 
                                    onClick={() => setBalanceType('locked')} 
                                    variant={balanceType === 'locked' ? 'default' : 'ghost'}
                                    size="sm"
                                    className={balanceType === 'locked' ? 'bg-brand-500 hover:bg-brand-600 text-white shadow-md' : 'hover:bg-slate-200 dark:hover:bg-slate-600'}
                                >
                                    {balance_titles('locked')}
                                </Button>
                            </div>
                        </div>
                        <div className="flex flex-col w-full gap-4 mt-4">
                            {
                                balanceType === 'locked' && lockedBalance && lockedBalance?.length && lockedBalance?.length > 0 ?
                                    // @ts-ignore
                                    <PieDiagram data={lockedBalance.map(i => ({ amount: i.usd_amount, name: i.name }))} />
                                    : null
                            }

                            {
                                balanceType === 'avaliable' && avaliableBalance && avaliableBalance?.length && avaliableBalance?.length > 0 ?
                                    // @ts-ignore
                                    <PieDiagram data={avaliableBalance.map(i => ({ amount: i.usd_amount, name: i.name }))} />
                                    : null
                            }
                            <div className="w-full mt-2 space-y-2 max-h-[400px] overflow-y-auto pr-2">
                                {
                                    balance &&
                                        balance.length > 0 ?
                                        balance.map((b, i) => (
                                            <BalanceItem
                                                key={`${balanceType}-${b.currency_code}-${b.id}-${i}`}
                                                amount={b.balance}
                                                amountUSD={b.usd_amount}
                                                fullName={b.name}
                                                shortName={b.currency_code}
                                                img={b.currency_code.toLowerCase()}
                                                type={b.type as 'crypto' | 'currency'}
                                                i={i}
                                            />
                                        ))
                                        : lockedError || avaliableError ?
                                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center p-4 text-sm gap-2 rounded-lg justify-between text-red-700 dark:text-red-400">
                                                An error occured while getting {balanceType} balance
                                                <button className="bg-red-600 hover:bg-red-700 text-white rounded-lg shrink-0 p-2 flex items-center gap-2 text-xs transition-colors" onClick={() => {
                                                    if (balanceType === 'locked') return retryLocked();
                                                    return retryAvaliable();
                                                }}>
                                                    {
                                                        avaliableLoading || lockedLoading ? <Loader2Icon width={15} height={15} className="shrink-0 animate-spin" /> : <>Try again<Repeat width={15} height={15} className="shrink-0" /></>
                                                    }
                                                </button>
                                            </div>
                                            : <div className="w-full p-6 text-center bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                                                {empty_titles('balance')}
                                            </div>
                                }
                            </div>
                        </div>
                    </Card>
                    <Card className="lg:col-span-2 min-h-[500px] overflow-hidden bg-surface border-surface-border shadow-lg">
                        <AdvancedRealTimeChart
                            container_id={"tradingview_widget_wrapper"}
                            copyrightStyles={{
                                link: { display: "none" },
                                parent: { display: "none" },
                            }}
                            width={"100%"}
                            height={"100%"}
                            symbol="COINBASE:BTCUSD"
                        />
                    </Card>
                </div>
            </ErrorBoundary>
            <ErrorBoundary place="Datatable + TRXMODAL">
                <Card className="bg-surface border-surface-border shadow-lg p-6">
                    <h2 className="text-xl font-bold mb-6 text-surface-foreground">{table_titles('last_transactions')}</h2>
                    <DataTable
                        columns={getColumns()}
                        // @ts-ignore
                        data={transactions?.transactions || []}
                        currentPage={page}
                        perPage={perPage}
                        setPerPage={setPerPage}
                        setCurrentPage={setPage}
                        // @ts-ignore
                        totalPages={transactions?.total_pages}
                        tableName="transactions"
                        isLoading={transactionsIsLoading || transactionsIsPending || transactionsIsFetching}
                        refetch={refetchTransactions}
                    />
                    <TransactionModal />
                </Card>
            </ErrorBoundary>
        </div>
    );
};

export default Page;